import { useState, useEffect, useCallback } from "react";
import { Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import { API } from "@/App";
import { 
  LayoutDashboard, 
  Users, 
  GraduationCap,
  CreditCard, 
  BarChart3,
  Menu,
  School,
  Settings,
  Upload,
  Megaphone,
  Home,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import NotificationBell from "@/components/NotificationBell";
import { 
  PortalSidebar, 
  LogoutModal,
} from "@/components/PortalLayout";
import SharedFooter from "@/components/SharedFooter";
import UnifiedPortalHeader from "@/components/UnifiedPortalHeader";
import { SchoolStatsBar } from "@/components/PortalStatsBar";

export default function SchoolLayout() {
  const [admin, setAdmin] = useState(null);
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showLoginNotifications, setShowLoginNotifications] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Handle browser back button - show logout confirmation
  useEffect(() => {
    const handlePopState = (event) => {
      event.preventDefault();
      window.history.pushState(null, '', window.location.href);
      setShowLogoutModal(true);
    };

    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const fetchAdminAndSchool = useCallback(async () => {
    try {
      const response = await fetch(`${API}/school/admin/me`, {
        credentials: "include",
      });
      
      let data = {};
      try {
        data = await response.json();
      } catch (parseError) {
        if (response.ok) {
          setLoading(false);
          return;
        }
      }

      if (response.ok && data.admin) {
        setAdmin(data.admin);
        
        // Fetch school profile
        const schoolResponse = await fetch(`${API}/school/profile`, {
          credentials: "include",
        });
        
        if (schoolResponse.ok) {
          const schoolData = await schoolResponse.json();
          setSchool(schoolData.school);
        }
      } else if (response.status === 401) {
        navigate("/school/login");
      } else if (!response.ok) {
        navigate("/school/login");
      }
    } catch (error) {
      console.error("School admin auth check error:", error);
      navigate("/school/login");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchAdminAndSchool();
  }, [fetchAdminAndSchool]);

  const handleLogout = async () => {
    try {
      await fetch(`${API}/school/admin/logout`, {
        method: "POST",
        credentials: "include",
      });
      
      toast.success("Logged out successfully");
      setShowLogoutModal(false);
      navigate("/");
    } catch (error) {
      toast.error("Logout failed");
      setShowLogoutModal(false);
      navigate("/");
    }
  };

  // Permission-based nav items
  const getNavItems = () => {
    const permissions = admin?.permissions || [];
    const items = [
      { path: "/school/dashboard", label: "Dashboard", icon: LayoutDashboard, permission: null },
    ];
    
    if (permissions.includes("manage_students") || permissions.includes("bulk_upload")) {
      items.push({ path: "/school/students", label: "Students", icon: Users, permission: "manage_students" });
    }
    
    if (permissions.includes("manage_teachers")) {
      items.push({ path: "/school/teachers", label: "Teachers", icon: GraduationCap, permission: "manage_teachers" });
    }
    
    if (permissions.includes("view_billing") || permissions.includes("manage_billing")) {
      items.push({ path: "/school/billing", label: "Billing", icon: CreditCard, permission: "view_billing" });
    }
    
    if (permissions.includes("view_analytics")) {
      items.push({ path: "/school/analytics", label: "Analytics", icon: BarChart3, permission: "view_analytics" });
    }
    
    if (permissions.includes("manage_students") || permissions.includes("manage_admins")) {
      items.push({ path: "/school/announcements", label: "Announcements", icon: Megaphone, permission: null });
    }
    
    if (permissions.includes("manage_admins")) {
      items.push({ path: "/school/settings", label: "Settings", icon: Settings, permission: "manage_admins" });
    }
    
    return items;
  };

  const navItems = getNavItems();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!admin) {
    return null;
  }

  // NotificationBell for rightContent
  const notificationBell = (
    <NotificationBell onLoginNotifications={showLoginNotifications} />
  );

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Unified Header matching landing page */}
      <UnifiedPortalHeader
        portalType="school"
        userName={admin.name}
        userSubtitle={admin.role?.replace("_", " ")}
        onLogout={() => setShowLogoutModal(true)}
        onMobileMenuClick={() => setSidebarOpen(true)}
        showMobileMenuButton={true}
        rightContent={notificationBell}
      />
      
      {/* School Stats Bar - Below header */}
      <SchoolStatsBar school={school} admin={admin} />

      <div className="flex flex-1">
        {/* Sidebar */}
        <PortalSidebar
          portalType="school"
          navItems={navItems}
          userName={admin.name}
          userSubtitle={admin.role?.replace("_", " ")}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          onLogout={() => setShowLogoutModal(true)}
          footerContent={
            (admin?.permissions || []).includes("bulk_upload") && (
              <Link
                to="/school/students/upload"
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg font-medium transition-all mb-3 ${
                  location.pathname === "/school/students/upload"
                    ? "bg-teal-100 text-teal-700"
                    : "text-teal-600 hover:bg-teal-50 border border-teal-200"
                }`}
              >
                <Upload className="w-5 h-5" />
                <span>Bulk Upload</span>
              </Link>
            )
          }
        />

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-h-[calc(100vh-56px)]">
          <main className="flex-1 p-4 lg:p-6">
            <Outlet context={{ admin, school, refreshSchool: fetchAdminAndSchool }} />
          </main>

          {/* Unified Footer matching landing page */}
          <SharedFooter />
        </div>
      </div>

      {/* Logout Modal */}
      <LogoutModal
        open={showLogoutModal}
        onOpenChange={setShowLogoutModal}
        onConfirm={handleLogout}
        portalType="school"
      />
    </div>
  );
}
